import axios from "axios";

export interface ChatMessage {
  id: string;
  sender: "user" | "bot";
  text: string;
  timestamp: string;
}

export interface ChatbotQueryResponse {
  success: boolean;
  response?: string;
  message?: string;
}

export interface VoiceNoteReport {
  system: any;
  complaints: any[];
  otherSymptoms: any[];
  history: any[];
  examinations: any[];
  impressions: any[];
  treatment_plans: any[];
  duration?: number | null;
  durationType?: string;
  complaintRemark?: string;
  otherSymptomRemark?: string;
  historyRemark?: string;
  impressionRemark?: string;
  examinationRemark?: string;
  treatmentRemark?: string;
}

export interface VoiceNoteResponse {
  success: boolean;
  report?: VoiceNoteReport;
  message?: string;
}

export const getAiServiceUrl = (): string => {
  if (process.env.REACT_APP_AI_SERVICE_URL) {
    return process.env.REACT_APP_AI_SERVICE_URL;
  }
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:8001";
  }
  return "https://ecn-ai.vercel.app";
};

export const sendChatbotQuery = async (query: string): Promise<string> => {
  const baseUrl = getAiServiceUrl();
  const endpoint = `${baseUrl}/chatbot/query`;

  try {
    const res = await axios.post<ChatbotQueryResponse>(
      endpoint,
      { query: query.trim() },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout:80000, // 1min 20s timeout
      }
    );

    if (res.data && res.data.success && res.data.response) {
      return res.data.response;
    } else if (res.data && res.data.message) {
      return res.data.message;
    } else {
      return "Please connect with customer support.";
    }
  } catch (error: any) {
    console.error("Chatbot API error:", error);
    if (error.code === "ECONNABORTED") {
      return "The request timed out. Please try again or connect with customer support.";
    }
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    return "Unable to connect to the AI Service. Please verify the AI Service is running on port 8001 or connect with customer support.";
  }
};

export const generateVoiceNote = async (
  transcript: string,
  catalog: Record<string, any[]>
): Promise<VoiceNoteReport> => {
  const response = await axios.post<VoiceNoteResponse>(
    `${getAiServiceUrl()}/chatbot/voice-note`,
    { transcript: transcript.trim(), catalog },
    { headers: { "Content-Type": "application/json" }, timeout: 120000 }
  );

  if (!response.data.success || !response.data.report) {
    throw new Error(response.data.message || "The AI could not create a note.");
  }
  return response.data.report;
};
